import { privateRequest } from "../axios";
import type { ExportFileResult, ListApiResponse } from "@/types/common";
import { parseFilenameFromContentDisposition } from "@/lib/downloadExcel";

export interface AuditLogEntry {
  id: number;
  actorId: number | null;
  actorName: string | null;
  action: string;
  module: string;
  fieldName: string | null;
  oldValue: unknown;
  newValue: unknown;
  ipAddress: string | null;
  createdAt: string;
}

export interface GetActionLogsParams {
  pageNo?: number;
  perPage?: number;
  search?: string | undefined;
  module?: string | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  // Real param, given directly by the user — `isExport: true` on this
  // same endpoint makes it return the finished export file directly (an
  // .xlsx, per a real confirmed response) instead of the usual JSON list
  // envelope. Only `exportActionLogs` below sets this; `getActionLogs`
  // never does.
  isExport?: boolean | undefined;
}

const ENDPOINTS = {
  ACTION_LOGS: "/action-logs",
};

// The backend only JSON-encodes `oldValue`/`newValue` correctly for
// null/boolean/number/object values — a plain string value (e.g. a
// changed `thumbnailUrl` or a chapter `name`) comes back as a bare,
// unquoted token, which is invalid JSON and makes the entire response
// fail to parse (that page's fetch rejects, and the table renders empty —
// see docs/decisions.md #68/#70). This is a backend serialization bug and
// the real fix belongs there — a client-side repair here was built,
// verified, then reverted on request once already, on the reasoning that
// the backend should own it. It recurred (the same bad row just shifts
// onto a different page as more records accumulate — see #70's second
// addendum), still blanking whatever page it lands on, so this is
// restored as an explicit stopgap while the backend fix is pending, not a
// replacement for it. Remove this once the backend's serializer quotes
// plain-string `oldValue`/`newValue` the same way it already does for
// every other value type on the same rows.
const repairBareValue = (segment: string): string => {
  const trimmed = segment.trim();
  if (trimmed === "null" || trimmed === "true" || trimmed === "false" || /^-?\d+(\.\d+)?$/.test(trimmed)) {
    return segment;
  }
  return JSON.stringify(trimmed);
};

const repairActionLogJson = (raw: string): string =>
  raw
    .replace(
      /("oldValue"\s*:\s*)([^"{}[\]\s][\s\S]*?)(,\s*"newValue")/g,
      (_match, prefix: string, value: string, tail: string) => `${prefix}${repairBareValue(value)}${tail}`,
    )
    .replace(
      /("newValue"\s*:\s*)([^"{}[\]\s][\s\S]*?)(,\s*"ipAddress")/g,
      (_match, prefix: string, value: string, tail: string) => `${prefix}${repairBareValue(value)}${tail}`,
    );

const getActionLogs = async (params?: GetActionLogsParams) => {
  const response = await privateRequest.get<ListApiResponse<AuditLogEntry>>(ENDPOINTS.ACTION_LOGS, {
    params,
    transformResponse: [
      (data: unknown) => {
        if (typeof data !== "string") return data;
        try {
          return JSON.parse(data);
        } catch {
          return JSON.parse(repairActionLogJson(data));
        }
      },
    ],
  });
  return response.data;
};

// Confirmed real response for `GET /admin/action-logs?isExport=true`,
// given directly by the user (response headers, not a body sample —
// `content-type: application/vnd.openxmlformats-officedocument.
// spreadsheetml.sheet`, `content-disposition: attachment;
// filename="activity_logs_03-09-2026_0627.xlsx"`). `responseType: "blob"`
// is required here — without it axios would try to decode the binary
// body as text/JSON. No `transformResponse`/JSON-repair override (see
// `getActionLogs` above) since there's no JSON to repair in a blob
// response.
//
// `fileName` genuinely does come back `null` against the real deployed
// backend (`VITE_API_BASE_URL=https://bfcdev.nimapinfotech.in` — a true
// cross-origin request from the dev server's own origin), confirmed by
// tracing `response.headers` end to end: the browser only exposes a
// CORS-safelisted subset of response headers to JS for a cross-origin
// request unless the server opts more in via
// `Access-Control-Expose-Headers` — `Content-Disposition` isn't in that
// safelist, and the header dump given for this endpoint has no
// `access-control-expose-headers` line. This isn't a frontend bug (the
// `parseFilenameFromContentDisposition` fallback in `ExportModal.tsx` is
// doing exactly what it's for); the backend needs to add
// `Access-Control-Expose-Headers: Content-Disposition` to this response
// (and to `/users`, `/chapters`'s export responses, if the real filename
// matters there too) for the browser to ever see it. See
// docs/decisions.md #75.
const exportActionLogs = async (params?: Omit<GetActionLogsParams, "isExport">): Promise<ExportFileResult> => {
  const response = await privateRequest.get(ENDPOINTS.ACTION_LOGS, {
    params: { ...params, isExport: true },
    responseType: "blob",
  });
  return {
    blob: response.data as Blob,
    fileName: parseFilenameFromContentDisposition(response.headers["content-disposition"]),
  };
};

const auditLogService = {
  getActionLogs,
  exportActionLogs,
};

export default auditLogService;
